import React, { useEffect, useState } from "react";
import { getProfile, getSkills } from "../../../../api/profileAPI";

// ─── Edit your "What I Do" cards here ─────────────────────────────────────────
// Each card has: title, color/border/accent/iconColor (Tailwind classes), icon JSX, and points array.
const WHAT_I_DO = [
  {
    title: "Cybersecurity",
    color: "from-red-900/30 to-zinc-900/50",
    border: "border-red-800/40",
    accent: "bg-red-500",
    iconColor: "text-red-400",
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    points: [
      "Vulnerability assessment for web apps & APIs (Nmap, Burp Suite, DVWA)",
      "Blue Team: log/packet analysis with Kibana (ELK), Wazuh SIEM, Wireshark, Nessus",
      "CTF-style labs: reconnaissance, enumeration, basic exploitation",
      "Security-first mindset with risk-based prioritization and reproducible reporting",
    ],
  },
  {
    title: "Network (NOC L1)",
    color: "from-blue-900/30 to-zinc-900/50",
    border: "border-blue-800/40",
    accent: "bg-blue-500",
    iconColor: "text-blue-400",
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
      </svg>
    ),
    points: [
      "Real-time monitoring: Grafana, OpManager, Dynatrace, Elastic",
      "Incident triage & escalation to L2/L3 in 24×7 rotational shift",
      "UiPath automation for NOC report generation",
      "Excel-based trend mapping and Jira ticketing",
    ],
  },
  {
    title: "Full-Stack Development",
    color: "from-cyan-900/30 to-zinc-900/50",
    border: "border-cyan-800/40",
    accent: "bg-cyan-500",
    iconColor: "text-cyan-400",
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
    points: [
      "React.js front-end with responsive, accessible UIs",
      "Custom Express + MySQL backend with REST API",
      "Structured QA: functional, regression, performance testing",
      "Multer file uploads, JWT auth, analytics tracking",
    ],
  },
];
// ──────────────────────────────────────────────────────────────────────────────

const About = () => {
  const [profile, setProfile] = useState(null);
  const [skills, setSkills] = useState([]);
  const [skillsLoaded, setSkillsLoaded] = useState(false);

  useEffect(() => {
    getProfile()
      .then((d) => setProfile(d.profile ?? d))
      .catch(() => {});
    getSkills()
      .then((data) => {
        setSkills(data);
        setSkillsLoaded(true);
      })
      .catch(() => setSkillsLoaded(true));
  }, []);

  const bio =
    profile?.description ||
    "I'm a cybersecurity-oriented engineer with a strong foundation in network operations. I currently serve as a Level 1 NOC Engineer in the banking sector, operating in a 24×7 rotational shift to help keep critical services available and reliable. My career focus is shifting toward vulnerability assessment, penetration testing, and incident response. Combining NOC experience with Front-End Development and Quality Assurance, I balance technical resilience, performance, and user impact.";

  const doingNow =
    profile?.doing_now ||
    "Cybersecurity · NOC Engineering · Full-Stack Development";

  const hardSkills = skills.filter((s) => s.category === "hard");
  const softSkills = skills.filter((s) => s.category === "soft");
  const hasSkills = skillsLoaded && (hardSkills.length > 0 || softSkills.length > 0);

  return (
    <div className="space-y-8 text-white">
      {/* Bio */}
      <section>
        <SectionHeading label="About Me" />
        <p className="text-zinc-300 leading-relaxed text-sm">{bio}</p>
        {doingNow && (
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-600/10 border border-blue-600/20 text-blue-400 text-xs font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
            {doingNow}
          </div>
        )}
      </section>

      {/* What I Do */}
      {profile?.show_what_i_do !== 0 && (
      <section>
        <SectionHeading label="What I Do" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {WHAT_I_DO.map((item) => (
            <div
              key={item.title}
              className={`rounded-xl border ${item.border} bg-gradient-to-br ${item.color} p-5 flex flex-col gap-3`}
            >
              <div className="flex items-center gap-3">
                <div className={`${item.iconColor}`}>{item.icon}</div>
                <h3 className="font-bold text-sm text-white">{item.title}</h3>
              </div>
              <ul className="space-y-1.5">
                {item.points.map((pt, i) => (
                  <li key={i} className="flex gap-2 text-xs text-zinc-300 leading-relaxed">
                    <span className={`mt-1.5 flex-shrink-0 w-1.5 h-1.5 rounded-full ${item.accent}`} />
                    {pt}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* Skills — only shown when API returns data */}
      {hasSkills && (
        <section>
          <SectionHeading label="Skills" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {hardSkills.length > 0 && (
              <SkillBox label="Hard Skills" skills={hardSkills} color="blue" />
            )}
            {softSkills.length > 0 && (
              <SkillBox label="Soft Skills" skills={softSkills} color="cyan" />
            )}
          </div>
        </section>
      )}
    </div>
  );
};

const SectionHeading = ({ label }) => (
  <div className="flex items-center gap-3 mb-4">
    <span className="w-1 h-5 rounded-full bg-blue-500" />
    <h2 className="text-sm font-black uppercase tracking-widest text-zinc-400">{label}</h2>
  </div>
);

const SkillBox = ({ label, skills, color }) => {
  const colors = {
    blue: { border: "border-blue-800/40", title: "text-blue-400", bar: "bg-blue-500" },
    cyan: { border: "border-cyan-800/40", title: "text-cyan-400", bar: "bg-cyan-500" },
  };
  const c = colors[color];
  return (
    <div className={`rounded-xl border ${c.border} bg-zinc-900/60 p-5`}>
      <h4 className={`text-xs font-black uppercase tracking-widest ${c.title} mb-4`}>{label}</h4>
      <div className="space-y-3">
        {skills.map((s) => (
          <div key={s.id}>
            <div className="flex justify-between mb-1">
              <span className="text-xs text-zinc-200 font-medium">{s.name}</span>
              {s.level != null && (
                <span className="text-[10px] text-zinc-500">{s.level}%</span>
              )}
            </div>
            {s.level != null && (
              <div className="h-1 rounded-full bg-zinc-700">
                <div
                  className={`h-1 rounded-full ${c.bar} transition-all duration-700`}
                  style={{ width: `${Math.min(s.level, 100)}%` }}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default About;
